import { useState, useEffect, useMemo } from "react";
import {
  X,
  Send,
  AlertCircle,
  Users,
  Clock,
  Calendar,
  RotateCw,
  Repeat,
  Sparkles,
  Check,
  CalendarDays,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isAdmin } from "../../lib/permissions";
import { getTodayDateString } from "../../lib/format";

const PRIORITIES = ["Low", "Normal", "High", "Urgent"];
const STATUSES = ["Pending", "In Progress", "Completed", "Overdue"];
const DEPT_SHORTCUTS = ["Sales", "Design", "Production", "Procurement", "Delivery", "Accounts", "Management"];

const WEEK_DAYS = [
  { key: "Monday", short: "Mon" },
  { key: "Tuesday", short: "Tue" },
  { key: "Wednesday", short: "Wed" },
  { key: "Thursday", short: "Thu" },
  { key: "Friday", short: "Fri" },
  { key: "Saturday", short: "Sat" },
  { key: "Sunday", short: "Sun" },
];

export default function AssignTaskModal({ task = null, user = null, onClose }) {
  const isEditingRecurring = Boolean(task && task.recurring_task_id);
  const isEditingOneTime = Boolean(task && task.task_id && !task.recurring_task_id);
  const isEditing = isEditingRecurring || isEditingOneTime;
  const isSuperOrAdmin = isAdmin(user);

  // Task Type: "one_time" | "recurring"
  const [taskType, setTaskType] = useState(() => {
    if (isEditingRecurring || task?.task_type === "recurring") return "recurring";
    return "one_time";
  });

  const [title, setTitle] = useState(task?.title || "");
  const [description, setDescription] = useState(task?.description || "");
  const [companyName, setCompanyName] = useState(task?.company_name || "Pinaki Solutions");
  const [companies, setCompanies] = useState([
    { name: "Pinaki Solutions" },
    { name: "Pinaki Prints" },
  ]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  // Multi-Assignee state: list of selected user IDs
  const [selectedUserIds, setSelectedUserIds] = useState(() => {
    if (task) {
      if (Array.isArray(task.assigned_to_ids) && task.assigned_to_ids.length > 0) {
        return task.assigned_to_ids;
      }
      if (Array.isArray(task.assignees) && task.assignees.length > 0) {
        return task.assignees.map((a) => a.user_id).filter(Boolean);
      }
      if (task.assigned_to_id) {
        return [task.assigned_to_id];
      }
    }
    return user?.user_id ? [user.user_id] : [];
  });

  const [department, setDepartment] = useState(task?.department || user?.department || "Sales");
  const [priority, setPriority] = useState(task?.priority || "Normal");
  const [status, setStatus] = useState(task?.status || "Pending");

  // One-Time timing
  const [dueDate, setDueDate] = useState(task?.due_date || "");
  const [dueTime, setDueTime] = useState(task?.due_time || "");
  const [startTime, setStartTime] = useState(task?.start_time || "");

  // Recurring settings
  const [frequency, setFrequency] = useState(task?.frequency || "weekly");
  const [selectedDays, setSelectedDays] = useState(() => {
    if (task?.days_of_week && Array.isArray(task.days_of_week) && task.days_of_week.length > 0) {
      return task.days_of_week;
    }
    return ["Friday"];
  });
  const [dayOfMonth, setDayOfMonth] = useState(task?.day_of_month || 1);
  const [timeOfDay, setTimeOfDay] = useState(task?.time_of_day || task?.due_time || "10:00");
  const [startDate, setStartDate] = useState(task?.start_date || getTodayDateString());
  const [hasEndDate, setHasEndDate] = useState(Boolean(task?.end_date));
  const [endDate, setEndDate] = useState(task?.end_date || "");

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Load active companies
  useEffect(() => {
    let mounted = true;
    api("get", "/companies?active_only=true")
      .then((res) => {
        if (mounted && Array.isArray(res.data) && res.data.length > 0) {
          setCompanies(res.data);
          if (!isEditing && !task) {
            setCompanyName((prev) => prev || res.data[0].name);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load companies for task assignment:", err);
      })
      .finally(() => {
        if (mounted) setLoadingCompanies(false);
      });

    return () => {
      mounted = false;
    };
  }, [isEditing, task]);

  // Load active users
  useEffect(() => {
    let mounted = true;
    api("get", "/users?active_only=true")
      .then((res) => {
        if (mounted && Array.isArray(res.data)) {
          const activeUsers = res.data.filter((u) => u.is_active !== false && !u.is_deleted);
          // Sort with current user first, then by name
          const sorted = [...activeUsers].sort((a, b) => {
            if (a.user_id === user?.user_id) return -1;
            if (b.user_id === user?.user_id) return 1;
            return (a.name || "").localeCompare(b.name || "");
          });
          setUsers(sorted);

          if (!isEditing && !task) {
            const selfId = user?.user_id || (sorted.length > 0 ? sorted[0].user_id : "");
            if (selfId) {
              setSelectedUserIds((prev) => (prev.length === 0 ? [selfId] : prev));
            }
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load users for task assignment:", err);
      })
      .finally(() => {
        if (mounted) setLoadingUsers(false);
      });

    return () => {
      mounted = false;
    };
  }, [isEditing, task, user]);

  const uniqueCompanyOptions = Array.from(
    new Set(
      [
        ...companies.map((c) => c.name),
        task?.company_name,
        "Pinaki Solutions",
        "Pinaki Prints",
      ].filter(Boolean)
    )
  );

  const handleAddUser = (userId) => {
    if (!userId) return;
    if (!selectedUserIds.includes(userId)) {
      const updated = [...selectedUserIds, userId];
      setSelectedUserIds(updated);
      setError("");

      const addedUser = users.find((u) => u.user_id === userId);
      if (addedUser && selectedUserIds.length === 0) {
        const userDepts =
          addedUser.departments && Array.isArray(addedUser.departments) && addedUser.departments.length > 0
            ? addedUser.departments
            : [addedUser.department];
        if (userDepts[0]) setDepartment(userDepts[0]);
      }
    }
  };

  const handleRemoveUser = (userId) => {
    setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
  };

  const handleAddDepartmentUsers = (deptName) => {
    const deptUids = users
      .filter((u) => {
        const userDepts =
          u.departments && Array.isArray(u.departments) && u.departments.length > 0
            ? u.departments
            : [u.department];
        return userDepts.some((d) => (d || "").toLowerCase() === deptName.toLowerCase());
      })
      .map((u) => u.user_id);

    if (deptUids.length > 0) {
      setSelectedUserIds((prev) => Array.from(new Set([...prev, ...deptUids])));
      setError("");
    }
  };

  const toggleDayOfWeek = (dayName) => {
    setSelectedDays((prev) => {
      if (prev.includes(dayName)) {
        if (prev.length === 1) return prev; // Keep at least one day
        return prev.filter((d) => d !== dayName);
      }
      return [...prev, dayName];
    });
  };

  // Recurrence summary text computation for live preview
  const recurrenceSummaryText = useMemo(() => {
    const timeFormatted = timeOfDay ? `at ${timeOfDay}` : "";
    if (frequency === "daily") {
      return `Every day ${timeFormatted}`;
    }
    if (frequency === "weekly") {
      if (selectedDays.length === 0) return `Weekly ${timeFormatted}`;
      if (selectedDays.length === 7) return `Every day ${timeFormatted}`;
      if (
        selectedDays.length === 5 &&
        ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].every((d) => selectedDays.includes(d))
      ) {
        return `Every Weekday (Mon–Fri) ${timeFormatted}`;
      }
      return `Every ${selectedDays.join(", ")} ${timeFormatted}`;
    }
    if (frequency === "monthly") {
      const suffix =
        11 <= dayOfMonth && dayOfMonth <= 13
          ? "th"
          : { 1: "st", 2: "nd", 3: "rd" }[dayOfMonth % 10] || "th";
      return `Monthly on the ${dayOfMonth}${suffix} ${timeFormatted}`;
    }
    if (frequency === "custom") {
      return `Selected days (${selectedDays.join(", ")}) ${timeFormatted}`;
    }
    return `Repeats ${frequency} ${timeFormatted}`;
  }, [frequency, selectedDays, dayOfMonth, timeOfDay]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError("Task title is required.");
      return;
    }

    if (!companyName.trim()) {
      setError("Company / Organization is required.");
      return;
    }

    if (selectedUserIds.length === 0) {
      setError("Please assign this task to at least one team member.");
      return;
    }

    if (taskType === "recurring") {
      if (!startDate) {
        setError("Start Date is required for recurring tasks.");
        return;
      }
      if (frequency === "weekly" && selectedDays.length === 0) {
        setError("Please select at least one day of the week.");
        return;
      }
      if (hasEndDate && endDate && endDate < startDate) {
        setError("End Date cannot be before Start Date.");
        return;
      }
    }

    setBusy(true);
    setError("");

    try {
      if (taskType === "recurring") {
        const recurringPayload = {
          title: title.trim(),
          description: description.trim(),
          company_name: companyName.trim(),
          assigned_to_ids: selectedUserIds,
          assigned_to_id: selectedUserIds[0],
          department,
          priority,
          frequency,
          days_of_week: frequency === "monthly" ? [] : selectedDays,
          day_of_month: frequency === "monthly" ? Number(dayOfMonth) : null,
          time_of_day: timeOfDay || "10:00",
          start_date: startDate,
          end_date: hasEndDate && endDate ? endDate : null,
        };

        if (isEditingRecurring) {
          await api("put", `/tasks/recurring/${task.recurring_task_id}`, recurringPayload);
          toast.success("Recurring task definition updated successfully!");
        } else {
          await api("post", "/tasks/recurring", recurringPayload);
          const count = selectedUserIds.length;
          toast.success(
            `Recurring task created! Scheduled ${recurrenceSummaryText} for ${count} ${
              count === 1 ? "assignee" : "assignees"
            }.`
          );
        }
      } else {
        // ONE-TIME TASK
        if (isEditingOneTime) {
          await api("put", `/tasks/${task.task_id}`, {
            title: title.trim(),
            description: description.trim(),
            company_name: companyName.trim(),
            assigned_to_ids: selectedUserIds,
            assigned_to_id: selectedUserIds[0],
            department,
            due_date: dueDate,
            due_time: dueTime,
            start_time: startTime,
            priority,
            status,
          });
          toast.success("Task updated successfully");
        } else {
          const payload = {
            task_type: "one_time",
            title: title.trim(),
            description: description.trim(),
            company_name: companyName.trim(),
            assigned_to_ids: selectedUserIds,
            assigned_to_id: selectedUserIds[0],
            department,
            due_date: dueDate,
            due_time: dueTime,
            start_time: startTime,
            priority,
          };

          await api("post", "/tasks", payload);
          const count = selectedUserIds.length;
          if (count > 1) {
            toast.success(`Task assigned to ${count} team members for ${companyName.trim()}!`);
          } else {
            toast.success(`Task created for ${companyName.trim()} and assigned successfully`);
          }
        }
      }
      onClose(true);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to save task. Please try again.";
      setError(typeof msg === "string" ? msg : "An unexpected error occurred.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" data-testid="assign-task-backdrop">
      <form
        className="modal"
        style={{ maxWidth: 640, width: "100%", maxHeight: "92vh", overflowY: "auto" }}
        onSubmit={handleSubmit}
        data-testid="assign-task-form"
      >
        {/* Modal Header */}
        <div className="modal-head" style={{ marginBottom: 14 }}>
          <div>
            <div className="small-label" style={{ letterSpacing: "1.2px", color: "#64748b" }}>
              {isEditingRecurring
                ? `EDIT RECURRING TASK · ${task.recurring_task_id}`
                : isEditingOneTime
                ? `EDIT TASK · ${task.task_id}`
                : "TASK ASSIGNMENT"}
            </div>
            <h2 style={{ fontSize: 19, marginTop: 4 }}>
              {isEditingRecurring
                ? "Edit Recurring Task"
                : isEditingOneTime
                ? "Edit Task"
                : "Assign Task"}
            </h2>
            <p className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              {taskType === "recurring"
                ? "Configure a recurring task once. Future occurrences and reminders are generated automatically."
                : "Assign a one-time CRM task to one or multiple team members across departments."}
            </p>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={() => onClose(false)}
            data-testid="assign-task-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        {/* TASK TYPE SELECTOR (Only when creating new task or switching) */}
        {!isEditing && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
              marginBottom: 16,
              padding: 4,
              background: "#f1f5f9",
              borderRadius: 8,
            }}
            data-testid="task-type-selector"
          >
            <button
              type="button"
              onClick={() => setTaskType("one_time")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                padding: "8px 12px",
                borderRadius: 6,
                border: "none",
                fontSize: 13,
                fontWeight: taskType === "one_time" ? 600 : 500,
                background: taskType === "one_time" ? "#ffffff" : "transparent",
                color: taskType === "one_time" ? "var(--navy)" : "#64748b",
                boxShadow: taskType === "one_time" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              data-testid="task-type-one-time-btn"
            >
              <Calendar size={14} /> One-Time Task
            </button>
            <button
              type="button"
              onClick={() => setTaskType("recurring")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                padding: "8px 12px",
                borderRadius: 6,
                border: "none",
                fontSize: 13,
                fontWeight: taskType === "recurring" ? 600 : 500,
                background: taskType === "recurring" ? "#ffffff" : "transparent",
                color: taskType === "recurring" ? "var(--navy)" : "#64748b",
                boxShadow: taskType === "recurring" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              data-testid="task-type-recurring-btn"
            >
              <RotateCw size={14} color={taskType === "recurring" ? "#2563eb" : "currentColor"} />
              <span>
                Recurring Task{" "}
                <span
                  style={{
                    fontSize: 10,
                    background: "#dbeafe",
                    color: "#1d4ed8",
                    padding: "1px 5px",
                    borderRadius: 4,
                    marginLeft: 3,
                    fontWeight: 700,
                  }}
                >
                  NEW
                </span>
              </span>
            </button>
          </div>
        )}

        {error && (
          <div className="error" style={{ marginBottom: 14 }} data-testid="assign-task-error">
            <AlertCircle size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
            {error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
          {/* Task Title */}
          <div>
            <label className="dispatch-label">
              Task Title <span style={{ color: "var(--red)" }}>*</span>
            </label>
            <input
              type="text"
              className="dispatch-control"
              placeholder={
                taskType === "recurring"
                  ? "e.g. Weekly Team Meeting, Friday Operations Review, Monthly Inventory Audit"
                  : "e.g. Client Meeting, Prepare Production Report, Follow up on Die-line"
              }
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
              data-testid="task-title-input"
            />
          </div>

          {/* Company / Organization Dropdown */}
          <div>
            <label className="dispatch-label">
              Company / Organization <span style={{ color: "var(--red)" }}>*</span>
            </label>
            <select
              className="dispatch-control"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              disabled={loadingCompanies}
              required
              data-testid="task-company-select"
            >
              {uniqueCompanyOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="dispatch-label">Description</label>
            <textarea
              className="dispatch-control"
              style={{ height: 68, resize: "vertical", padding: "8px 12px" }}
              placeholder="Provide context, meeting agenda, or specific recurring deliverables..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="task-description-input"
            />
          </div>

          {/* MULTI-USER ASSIGNEE SELECTION FIELD */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 6,
              }}
            >
              <label className="dispatch-label" style={{ margin: 0 }}>
                Assigned To <span style={{ color: "var(--red)" }}>*</span>
                <span style={{ fontWeight: 400, color: "#64748b", marginLeft: 6, fontSize: 11.5 }}>
                  ({selectedUserIds.length} {selectedUserIds.length === 1 ? "user" : "users"} selected)
                </span>
              </label>
            </div>

            {/* Selected Users Chips Container */}
            <div
              style={{
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: "8px 10px",
                background: "#fbfcfe",
                minHeight: 75,
              }}
              data-testid="multi-assignee-container"
            >
              {selectedUserIds.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  {selectedUserIds.map((uid) => {
                    const matchedUser =
                      users.find((u) => u.user_id === uid) ||
                      (task?.assignees || []).find((a) => a.user_id === uid);
                    const name = matchedUser?.name || uid;
                    const isSelf = uid === user?.user_id;

                    const userDepts =
                      matchedUser?.departments &&
                      Array.isArray(matchedUser.departments) &&
                      matchedUser.departments.length > 0
                        ? matchedUser.departments.join(" · ")
                        : matchedUser?.department || "";

                    return (
                      <span
                        key={uid}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          background: isSelf ? "#eff6ff" : "#f1f5f9",
                          border: isSelf ? "1px solid #bfdbfe" : "1px solid #cbd5e1",
                          color: isSelf ? "#1d4ed8" : "#334155",
                          borderRadius: 6,
                          padding: "3px 8px 3px 10px",
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                        data-testid={`selected-chip-${uid}`}
                      >
                        <span>
                          <b>{name}</b> {isSelf && <span style={{ color: "#2563eb", fontSize: 11 }}>(You)</span>}
                          {userDepts && (
                            <small style={{ color: "#64748b", marginLeft: 4, fontWeight: 400 }}>
                              · {userDepts}
                            </small>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveUser(uid)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#94a3b8",
                            padding: 2,
                            display: "flex",
                            alignItems: "center",
                            borderRadius: "50%",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = "#dc2626")}
                          onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}
                          title={`Remove ${name}`}
                          data-testid={`remove-user-btn-${uid}`}
                        >
                          <X size={13} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 8, fontStyle: "italic" }}>
                  No team members assigned yet. Please select users below.
                </div>
              )}

              {/* Add User Dropdown */}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <select
                  className="dispatch-control"
                  style={{ height: 35, fontSize: 12.5 }}
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddUser(e.target.value);
                    }
                  }}
                  disabled={loadingUsers}
                  data-testid="add-assignee-select"
                >
                  <option value="">
                    {loadingUsers ? "Loading team members..." : "+ Add team member..."}
                  </option>
                  {users
                    .filter((u) => !selectedUserIds.includes(u.user_id))
                    .map((u) => {
                      const isSelf = u.user_id === user?.user_id;
                      const userDepts =
                        u.departments && Array.isArray(u.departments) && u.departments.length > 0
                          ? u.departments.join(" · ")
                          : u.department || "General";
                      return (
                        <option key={u.user_id} value={u.user_id}>
                          + {u.name} — {userDepts} {isSelf ? "(You)" : ""}
                        </option>
                      );
                    })}
                </select>
              </div>

              {/* Quick Department Shortcuts for Admins */}
              {isSuperOrAdmin && (
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 7, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 10.5, color: "#64748b", marginRight: 2 }}>Quick add by dept:</span>
                  {DEPT_SHORTCUTS.map((dept) => (
                    <button
                      key={dept}
                      type="button"
                      className="outline-btn compact"
                      onClick={() => handleAddDepartmentUsers(dept)}
                      style={{ fontSize: 10, height: 20, padding: "0 6px" }}
                      title={`Add all ${dept} members`}
                    >
                      + {dept}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RECURRING TASK SCHEDULE SETTINGS CARD */}
          {taskType === "recurring" ? (
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                padding: "12px 14px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
              data-testid="recurrence-settings-card"
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <RotateCw size={14} color="#2563eb" />
                  <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--navy)" }}>
                    Recurrence Schedule
                  </span>
                </div>
                <span
                  style={{
                    fontSize: 11,
                    color: "#2563eb",
                    fontWeight: 600,
                    background: "#eff6ff",
                    padding: "2px 8px",
                    borderRadius: 12,
                    border: "1px solid #bfdbfe",
                  }}
                >
                  {recurrenceSummaryText}
                </span>
              </div>

              {/* Recurrence Frequency Buttons */}
              <div>
                <label className="dispatch-label" style={{ fontSize: 11.5, marginBottom: 5 }}>
                  Recurrence Frequency
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
                  {[
                    { id: "daily", label: "Daily" },
                    { id: "weekly", label: "Weekly" },
                    { id: "monthly", label: "Monthly" },
                    { id: "custom", label: "Custom Days" },
                  ].map((f) => {
                    const isSelected = frequency === f.id;
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => setFrequency(f.id)}
                        style={{
                          padding: "6px 8px",
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: isSelected ? 600 : 500,
                          border: isSelected ? "1px solid #2563eb" : "1px solid #cbd5e1",
                          background: isSelected ? "#eff6ff" : "#ffffff",
                          color: isSelected ? "#1d4ed8" : "#475569",
                          cursor: "pointer",
                          transition: "all 0.1s ease",
                        }}
                        data-testid={`frequency-${f.id}`}
                      >
                        {f.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* WEEKLY & CUSTOM: Day of the Week Selector */}
              {(frequency === "weekly" || frequency === "custom") && (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                    <label className="dispatch-label" style={{ fontSize: 11.5, margin: 0 }}>
                      Select Day(s) of the Week <span style={{ color: "var(--red)" }}>*</span>
                    </label>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedDays(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"])
                        }
                        style={{
                          fontSize: 10,
                          color: "#2563eb",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: "1px 4px",
                          textDecoration: "underline",
                        }}
                      >
                        Weekdays
                      </button>
                      <span style={{ color: "#cbd5e1", fontSize: 10 }}>·</span>
                      <button
                        type="button"
                        onClick={() => setSelectedDays(WEEK_DAYS.map((w) => w.key))}
                        style={{
                          fontSize: 10,
                          color: "#2563eb",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          padding: "1px 4px",
                          textDecoration: "underline",
                        }}
                      >
                        All 7 Days
                      </button>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 5 }}>
                    {WEEK_DAYS.map((w) => {
                      const active = selectedDays.includes(w.key);
                      return (
                        <button
                          key={w.key}
                          type="button"
                          onClick={() => toggleDayOfWeek(w.key)}
                          style={{
                            padding: "6px 2px",
                            borderRadius: 6,
                            fontSize: 11.5,
                            fontWeight: active ? 700 : 500,
                            border: active ? "1px solid #2563eb" : "1px solid #cbd5e1",
                            background: active ? "#2563eb" : "#ffffff",
                            color: active ? "#ffffff" : "#475569",
                            cursor: "pointer",
                            transition: "all 0.1s ease",
                            textAlign: "center",
                          }}
                          data-testid={`day-btn-${w.short}`}
                          title={`Repeat every ${w.key}`}
                        >
                          {w.short}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* MONTHLY: Day of Month Selector */}
              {frequency === "monthly" && (
                <div>
                  <label className="dispatch-label" style={{ fontSize: 11.5 }}>
                    Day of the Month
                  </label>
                  <select
                    className="dispatch-control"
                    value={dayOfMonth}
                    onChange={(e) => setDayOfMonth(Number(e.target.value))}
                    data-testid="day-of-month-select"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
                      const suffix =
                        11 <= d && d <= 13
                          ? "th"
                          : { 1: "st", 2: "nd", 3: "rd" }[d % 10] || "th";
                      return (
                        <option key={d} value={d}>
                          Every month on the {d}{suffix}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              {/* Timing & Dates: Time of Occurrence, Start Date, End Date */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1.1fr 1.1fr", gap: 10 }}>
                <div>
                  <label className="dispatch-label" style={{ fontSize: 11.5 }}>
                    <Clock size={11} style={{ marginRight: 3, verticalAlign: "middle" }} /> Time of Day
                  </label>
                  <input
                    type="time"
                    className="dispatch-control"
                    value={timeOfDay}
                    onChange={(e) => setTimeOfDay(e.target.value)}
                    required
                    data-testid="recurring-time-input"
                  />
                </div>

                <div>
                  <label className="dispatch-label" style={{ fontSize: 11.5 }}>
                    <Calendar size={11} style={{ marginRight: 3, verticalAlign: "middle" }} /> Start Date <span style={{ color: "var(--red)" }}>*</span>
                  </label>
                  <input
                    type="date"
                    className="dispatch-control"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                    data-testid="recurring-startdate-input"
                  />
                </div>

                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                    <label className="dispatch-label" style={{ fontSize: 11.5, margin: 0 }}>
                      <Calendar size={11} style={{ marginRight: 3, verticalAlign: "middle" }} /> End Date
                    </label>
                    <label style={{ fontSize: 10.5, color: "#64748b", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 3 }}>
                      <input
                        type="checkbox"
                        checked={hasEndDate}
                        onChange={(e) => {
                          setHasEndDate(e.target.checked);
                          if (!e.target.checked) setEndDate("");
                        }}
                        style={{ margin: 0 }}
                      />
                      Optional
                    </label>
                  </div>
                  <input
                    type="date"
                    className="dispatch-control"
                    value={endDate}
                    disabled={!hasEndDate}
                    min={startDate || getTodayDateString()}
                    onChange={(e) => setEndDate(e.target.value)}
                    placeholder="No end date (Ongoing)"
                    data-testid="recurring-enddate-input"
                  />
                </div>
              </div>
            </div>
          ) : (
            /* ONE-TIME TASK TIMING */
            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr", gap: 10 }}>
              <div>
                <label className="dispatch-label">
                  <Calendar size={11} style={{ marginRight: 3, verticalAlign: "middle" }} /> Due Date
                </label>
                <input
                  type="date"
                  className="dispatch-control"
                  value={dueDate}
                  min={getTodayDateString()}
                  onChange={(e) => setDueDate(e.target.value)}
                  data-testid="task-duedate-input"
                />
              </div>

              <div>
                <label className="dispatch-label">
                  <Clock size={11} style={{ marginRight: 3, verticalAlign: "middle" }} /> Due Time
                </label>
                <input
                  type="time"
                  className="dispatch-control"
                  value={dueTime}
                  onChange={(e) => setDueTime(e.target.value)}
                  placeholder="e.g. 16:00"
                  data-testid="task-duetime-input"
                />
              </div>

              <div>
                <label className="dispatch-label">
                  <Clock size={11} style={{ marginRight: 3, verticalAlign: "middle" }} /> Start Time (Opt)
                </label>
                <input
                  type="time"
                  className="dispatch-control"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  data-testid="task-starttime-input"
                />
              </div>
            </div>
          )}

          {/* Priority (and Status if editing one-time task) */}
          {isEditingOneTime ? (
            <div className="form-grid">
              <div>
                <label className="dispatch-label">Priority</label>
                <select
                  className="dispatch-control"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  data-testid="task-priority-select"
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="dispatch-label">Status</label>
                <select
                  className="dispatch-control"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  data-testid="task-status-select"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div>
              <label className="dispatch-label">Priority</label>
              <select
                className="dispatch-control"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                data-testid="task-priority-select"
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 20,
            paddingTop: 14,
            borderTop: "1px solid var(--border)",
          }}
        >
          <button
            type="button"
            className="outline-btn"
            onClick={() => onClose(false)}
            disabled={busy}
            data-testid="assign-task-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="primary-btn"
            disabled={busy || selectedUserIds.length === 0}
            data-testid="assign-task-submit-btn"
          >
            <Send size={13} style={{ marginRight: 4 }} />
            {busy
              ? "Saving..."
              : isEditingRecurring
              ? "Update Recurring Task"
              : isEditingOneTime
              ? "Update Task"
              : taskType === "recurring"
              ? "Create Recurring Task"
              : selectedUserIds.length > 1
              ? `Assign to ${selectedUserIds.length} Users`
              : "Assign Task"}
          </button>
        </div>
      </form>
    </div>
  );
}
